import { useState, useEffect } from "react";

const API_URL = process.env.REACT_APP_API_URL || "https://api.bearing-intelligence.com";

const field = (label, value) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ color: "#c4a563", fontSize: 10, fontWeight: "bold", letterSpacing: "0.6px", marginBottom: 4 }}>
      {label}
    </div>
    <div style={{ color: "#c8c9cd", fontSize: 13, lineHeight: 1.6 }}>
      {typeof value === "object" ? JSON.stringify(value, null, 2) : String(value || "—")}
    </div>
  </div>
);

function EditionCard({ edition, onApprove, onReject }) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);

  const execSum = (() => {
    try { return typeof edition.executive_summary === "string" ? JSON.parse(edition.executive_summary) : edition.executive_summary; }
    catch { return {}; }
  })();
  const pmesii = (() => {
    try { return typeof edition.operational_environment === "string" ? JSON.parse(edition.operational_environment) : edition.operational_environment; }
    catch { return {}; }
  })();
  const coas = (() => {
    try { return typeof edition.courses_of_action === "string" ? JSON.parse(edition.courses_of_action) : edition.courses_of_action; }
    catch { return []; }
  })();
  const changes = (() => {
    try { return typeof edition.week_over_week_changes === "string" ? JSON.parse(edition.week_over_week_changes) : edition.week_over_week_changes; }
    catch { return []; }
  })();

  const weekLabel = `${new Date(edition.week_starting).toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${new Date(edition.week_ending).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;

  return (
    <div style={{ backgroundColor: "#0f1419", border: "1px solid rgba(196,165,99,0.2)", borderRadius: 8, marginBottom: 12, overflow: "hidden" }}>
      {/* Header */}
      <div style={{ padding: "14px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
        onClick={() => setExpanded(e => !e)}>
        <div>
          <div style={{ color: "#c4a563", fontFamily: "Georgia,serif", fontSize: 16, fontWeight: "bold", marginBottom: 4 }}>
            Sunday Edition — Week of {weekLabel}
          </div>
          <div style={{ color: "#555", fontSize: 11 }}>
            {Array.isArray(execSum?.key_judgments) ? `${execSum.key_judgments.length} key judgments` : "Draft ready for review"}
            {" · "}
            <span style={{ color: edition.status === "pending_approval" ? "#c4a563" : edition.status === "published" ? "#44aa66" : "#888" }}>
              {edition.status === "pending_approval" ? "Pending Review" : edition.status === "published" ? "Published" : edition.status}
            </span>
          </div>
        </div>
        <span style={{ color: "#555", fontSize: 12 }}>{expanded ? "▲" : "▼"}</span>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div style={{ padding: "0 16px 16px", borderTop: "1px solid rgba(255,255,255,0.05)" }}>

          {/* Key Judgments */}
          {Array.isArray(execSum?.key_judgments) && execSum.key_judgments.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ color: "#c4a563", fontSize: 10, fontWeight: "bold", letterSpacing: "0.6px", marginBottom: 8 }}>KEY JUDGMENTS</div>
              {execSum.key_judgments.map((j, i) => (
                <div key={i} style={{ display: "flex", gap: 10, marginBottom: 8, padding: "8px 10px", backgroundColor: "rgba(196,165,99,0.04)", borderLeft: "2px solid rgba(196,165,99,0.3)", borderRadius: "0 4px 4px 0" }}>
                  <span style={{ color: "#c4a563", fontWeight: "bold", flexShrink: 0, fontSize: 12 }}>{i + 1}.</span>
                  <span style={{ color: "#c8c9cd", fontSize: 13, lineHeight: 1.6 }}>{j}</span>
                </div>
              ))}
            </div>
          )}

          {/* Week summary */}
          {execSum?.week_summary && field("WEEK SUMMARY", execSum.week_summary)}

          {/* Trend */}
          {execSum?.overall_threat_trend && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ color: "#c4a563", fontSize: 10, fontWeight: "bold", letterSpacing: "0.6px", marginBottom: 4 }}>OVERALL TREND</div>
              <span style={{ padding: "2px 9px", borderRadius: 4, fontSize: 11, fontWeight: "bold",
                backgroundColor: execSum.overall_threat_trend === "escalating" ? "rgba(255,51,51,0.1)" : execSum.overall_threat_trend === "de-escalating" ? "rgba(68,170,102,0.1)" : "rgba(196,165,99,0.1)",
                color: execSum.overall_threat_trend === "escalating" ? "#ff5555" : execSum.overall_threat_trend === "de-escalating" ? "#44aa66" : "#c4a563",
                border: "1px solid currentColor", textTransform: "uppercase", letterSpacing: "0.5px"
              }}>
                {execSum.overall_threat_trend}
              </span>
            </div>
          )}

          {/* Week-over-week changes */}
          {Array.isArray(changes) && changes.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ color: "#c4a563", fontSize: 10, fontWeight: "bold", letterSpacing: "0.6px", marginBottom: 8 }}>WEEK-OVER-WEEK CHANGES</div>
              {changes.map((c, i) => (
                <div key={i} style={{ padding: "8px 10px", backgroundColor: "#0a0d14", borderRadius: 6, marginBottom: 6, border: "1px solid rgba(255,255,255,0.05)" }}>
                  <div style={{ color: "#e8e9ed", fontWeight: "bold", fontSize: 12, marginBottom: 3 }}>{c.topic}</div>
                  <div style={{ display: "flex", gap: 8, fontSize: 11, color: "#666" }}>
                    <span>Before: <span style={{ color: "#888" }}>{c.previous}</span></span>
                    <span>→</span>
                    <span>Now: <span style={{ color: "#c8c9cd" }}>{c.current}</span></span>
                  </div>
                  {c.significance && <div style={{ color: "#555", fontSize: 11, marginTop: 3 }}>{c.significance}</div>}
                </div>
              ))}
            </div>
          )}

          {/* PMESII */}
          {pmesii && Object.keys(pmesii).length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ color: "#c4a563", fontSize: 10, fontWeight: "bold", letterSpacing: "0.6px", marginBottom: 8 }}>PMESII ASSESSMENT</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 6 }}>
                {Object.entries(pmesii).map(([domain, data]) => (
                  <div key={domain} style={{ padding: "9px 11px", backgroundColor: "#0a0d14", borderRadius: 6, border: "1px solid rgba(255,255,255,0.05)" }}>
                    <div style={{ color: "#c4a563", fontSize: 10, fontWeight: "bold", letterSpacing: "0.5px", marginBottom: 4, textTransform: "uppercase" }}>{domain}</div>
                    <div style={{ color: "#888", fontSize: 11, lineHeight: 1.5 }}>{data?.assessment || String(data)}</div>
                    {data?.trend && <div style={{ color: "#555", fontSize: 10, marginTop: 3 }}>Trend: {data.trend}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* COAs */}
          {Array.isArray(coas) && coas.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ color: "#c4a563", fontSize: 10, fontWeight: "bold", letterSpacing: "0.6px", marginBottom: 8 }}>COURSES OF ACTION</div>
              {coas.map((coa, i) => (
                <div key={i} style={{ padding: "9px 11px", backgroundColor: "#0a0d14", borderRadius: 6, marginBottom: 6, border: "1px solid rgba(255,255,255,0.05)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
                    <div style={{ color: "#e8e9ed", fontWeight: "bold", fontSize: 12 }}>{coa.coa}</div>
                    <span style={{ fontSize: 10, padding: "1px 7px", borderRadius: 4, flexShrink: 0, marginLeft: 8,
                      backgroundColor: coa.probability === "high" ? "rgba(255,51,51,0.1)" : coa.probability === "medium" ? "rgba(196,165,99,0.1)" : "rgba(68,170,102,0.1)",
                      color: coa.probability === "high" ? "#ff5555" : coa.probability === "medium" ? "#c4a563" : "#44aa66",
                      border: "1px solid currentColor", textTransform: "uppercase", fontWeight: "bold"
                    }}>{coa.probability}</span>
                  </div>
                  <div style={{ color: "#777", fontSize: 11, lineHeight: 1.5 }}>{coa.description}</div>
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          {edition.status === "pending_approval" && (
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button onClick={async () => { setLoading(true); await onApprove(edition.id); setLoading(false); }}
                disabled={loading}
                style={{ flex: 1, padding: "9px 0", backgroundColor: "rgba(68,170,102,0.15)",
                  border: "1px solid #44aa66", color: "#44aa66", borderRadius: 6,
                  cursor: loading ? "wait" : "pointer", fontWeight: "bold", fontSize: 13 }}>
                {loading ? "Publishing…" : "✓ Approve & Publish"}
              </button>
              <button onClick={async () => { setLoading(true); await onReject(edition.id); setLoading(false); }}
                disabled={loading}
                style={{ padding: "9px 16px", backgroundColor: "rgba(204,68,68,0.1)",
                  border: "1px solid #cc4444", color: "#cc4444", borderRadius: 6,
                  cursor: loading ? "wait" : "pointer", fontWeight: "bold", fontSize: 13 }}>
                Reject
              </button>
            </div>
          )}
          {edition.status === "published" && (
            <div style={{ color: "#44aa66", fontSize: 12, marginTop: 12 }}>
              ✓ Published {new Date(edition.published_at).toLocaleString("en-US", { timeZone: "America/New_York" })} ET
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function SundayEditionAdmin({ token }) {
  const [editions, setEditions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending_approval");
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchEditions = async () => {
    setLoading(true);
    try {
      const endpoint = filter === "published"
        ? `${API_URL}/api/sunday-editions`
        : `${API_URL}/api/sunday-editions/pending`;
      const res = await fetch(endpoint, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setEditions(data.editions || []);
    } catch { showToast("Failed to load Sunday Editions", "error"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchEditions(); }, [filter]); // eslint-disable-line

  const handleApprove = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/sunday-editions/${id}/approve`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error();
      showToast("Sunday Edition published ✓");
      fetchEditions();
    } catch { showToast("Approval failed", "error"); }
  };

  const handleReject = async (id) => {
    try {
      await fetch(`${API_URL}/api/sunday-editions/${id}/reject`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast("Edition rejected");
      fetchEditions();
    } catch { showToast("Rejection failed", "error"); }
  };

  return (
    <div>
      {toast && (
        <div style={{ position: "fixed", top: 16, right: 16, zIndex: 1000,
          padding: "10px 16px", borderRadius: 6, fontSize: 13, fontWeight: "bold",
          backgroundColor: toast.type === "error" ? "#cc4444" : "#44aa66", color: "white" }}>
          {toast.msg}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2 style={{ color: "#c4a563", fontFamily: "Georgia,serif", margin: 0, fontSize: 20 }}>
          Sunday Editions
        </h2>
        <div style={{ display: "flex", gap: 6 }}>
          {["pending_approval", "published"].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{
              padding: "5px 12px", borderRadius: 5, cursor: "pointer", fontSize: 11, fontWeight: "bold",
              backgroundColor: filter === f ? "rgba(196,165,99,0.15)" : "transparent",
              border: `1px solid ${filter === f ? "#c4a563" : "rgba(255,255,255,0.1)"}`,
              color: filter === f ? "#c4a563" : "#666"
            }}>
              {f === "pending_approval" ? "Pending Review" : "Published"}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "#c4a563" }}>Loading…</div>
      ) : editions.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: "#555", fontSize: 13 }}>
          {filter === "pending_approval"
            ? "No Sunday Editions pending review. Next edition generates Sunday at 0600 ET."
            : "No published Sunday Editions yet."}
        </div>
      ) : (
        editions.map(e => (
          <EditionCard key={e.id} edition={e} onApprove={handleApprove} onReject={handleReject} />
        ))
      )}
    </div>
  );
}
